import { Label, Text } from "@primer/react";
import { MButton, MCard } from "../../../components/mobile";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { IMyRegistration } from "../../../model/respone/registration/IMyRegistration";
import TkbAgenda from "./TkbAgenda";

interface IRegisteredCardProps {
    row: IMyRegistration;
    /** Đã chốt dữ liệu → ẩn nút Hủy mọi lớp. */
    isChotDuLieu: boolean;
    /** Lớp này đang gửi yêu cầu hủy. */
    isCancelling: boolean;
    /** Lý do khóa cổng — disable nút Hủy khi ngoài giờ. */
    gateReason?: string;
    onCancel: (row: IMyRegistration) => void;
}

/**
 * 1 lớp đã đăng ký → 1 thẻ (thay cho 1 row bảng #Registered desktop).
 */
const RegisteredCard = ({ row, isChotDuLieu, isCancelling, gateReason, onCancel }: IRegisteredCardProps) => {
    // Mirror desktop: nút Hủy chỉ hiện khi chưa chốt & chưa được Phòng ĐT duyệt.
    const canCancel = !isChotDuLieu && !row.duyet_dang_ky;
    // Hiển thị tiền chỉ khi đợt bật tinh_hoc_phi_theo_so_du. Snapshot tại register-time đã lưu
    // trong row.so_tien*, không phụ thuộc cờ hiện tại của lớp; nhưng giữ gate theo cờ để đảm bảo
    // đợt thường (tắt cờ) không hiển thị tiền dù DB có data legacy.
    const showMoney = useAppSelector(s => s.financial.data?.tinh_hoc_phi_theo_so_du === true);

    return (
        <MCard registered>
            <div className="m-card-head">
                <div className="m-card-head-main">
                    {row.ma_hoc_phan && <div className="m-card-code">{row.ma_hoc_phan}</div>}
                    <p className="m-card-title">{row.ten_hoc_phan ?? "—"}</p>
                    <div className="m-meta">
                        {row.so_tin_chi != null && (
                            <span className="m-meta-item">
                                <i className="fas fa-graduation-cap" />
                                <span className="m-meta-strong">{row.so_tin_chi} TC</span>
                            </span>
                        )}
                        <span className="m-meta-item">
                            <i className="fas fa-chalkboard" />
                            {row.ten_lop_hp ?? `#${row.id_lop_tc}`}
                        </span>
                        {row.loai_lop && (
                            <span className="m-meta-item">
                                <span className="m-meta-label">Loại</span>
                                {row.loai_lop}
                            </span>
                        )}
                    </div>
                    {(row.hinh_thuc_dang_ky_text || row.tinh_chat_lop_text) && (
                        <div className="m-badge-wrap">
                            {row.hinh_thuc_dang_ky_text && (
                                <Label variant="accent">{row.hinh_thuc_dang_ky_text}</Label>
                            )}
                            {row.tinh_chat_lop_text && (
                                <Label variant="success">{row.tinh_chat_lop_text}</Label>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="m-card-body">
                <TkbAgenda tkbs={row.thoiKhoaBieus} classLevelGv={row.giao_vien} />
                {showMoney && (
                    <Text sx={{ display: "block", fontSize: 0, color: "fg.muted", mt: "6px" }}>
                        Số tiền: <b>{(row.so_tien ?? 0).toLocaleString("vi-VN")}đ</b>
                        {" · "}Miễn giảm: <b>{(row.so_tien_mien_giam ?? 0).toLocaleString("vi-VN")}đ</b>
                        {" · "}Phải nộp: <b>{(row.so_tien_phai_nop ?? 0).toLocaleString("vi-VN")}đ</b>
                    </Text>
                )}
            </div>

            <div className="m-card-foot">
                {/* {row.duyet_dang_ky ? (
                    <Text sx={{ color: "success.fg", fontWeight: 600, fontSize: 1 }}>
                        <i className="fas fa-circle-check" /> Đã duyệt
                    </Text>
                ) : (
                    <Text sx={{ color: "attention.fg", fontWeight: 600, fontSize: 1 }}>
                        <i className="fas fa-clock" /> Chưa duyệt
                    </Text>
                )} */}
                <div className="m-card-foot-spacer" />
                {canCancel && (
                    <MButton
                        variant="danger"
                        loading={isCancelling}
                        disabled={!!gateReason}
                        title={gateReason || "Huỷ đăng ký lớp này"}
                        onClick={() => onCancel(row)}
                    >
                        Hủy đăng ký
                    </MButton>
                )}
            </div>
        </MCard>
    );
};

export default RegisteredCard;
